import { useState, useCallback } from "react";
import useGameData from "app/game-data/useGameData";
import { useScoreState } from "app/score/useScore";
import { type ScoreState } from "app/score/scoreState";
import formatPublishDate from "app/utils/formatPublishDate";

const GAMES_HOME_URL = import.meta.env.VITE_GAMES_HOME_URL as string;
const GAME_SHARE_URL = `${GAMES_HOME_URL}/spellcheck`;

export default function useShareText() {
  const gameData = useGameData();
  const scoreState = useScoreState();
  const [isCopied, setCopied] = useState<boolean>(false);

  const date = gameData ? `(${formatPublishDate(gameData.publishDate)})` : "";

  let shareText = `Spellcheck: ${date}`;
  shareText += `\n${getRoundScoreString(scoreState)}`;
  shareText += `\nWord points: ${getPreMultScore(scoreState)}`;
  shareText += `\nFinal multiplier: x${getFinalRoundMultiplier(scoreState).toFixed(1)}`;
  shareText += `\nFinal score: ${scoreState.gameScore}`;
  // shareText += `\nBest word: ${getHighestScoringWord(scoreState)}`;
  shareText += `\n`;
  shareText += `\nThink you can do better?`;
  shareText += `\n${GAME_SHARE_URL}?utm_campaign=tedspread&utm_medium=referral&utm_source=tedcomshare`;

  const copyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
    } catch (clipboardError) {
      console.error("Failed to copy to clipboard: ", clipboardError);
    }
  }, [shareText]);

  const navigatorShareText = useCallback(async () => {
    const shareData: ShareData = {
      title: "Spellcheck",
      text: shareText,
      // url: GAME_SHARE_URL,
    };

    try {
      if (!navigator.canShare(shareData)) {
        throw new Error("Navigator can't share data");
      }

      await navigator.share(shareData);
    } catch (error) {
      copyToClipboard();
      console.error("Failed to share: ", error);
    }
  }, [shareText, copyToClipboard]);

  return {
    text: shareText,
    isCopied,
    setCopied,
    navigatorShareText: navigatorShareText,
    copyToClipboard: copyToClipboard,
  };
}

function getRoundScoreString(scoreState: ScoreState): string {
  const keycapEmojis = ["1️⃣", "2️⃣", "3️⃣", "4️⃣", "5️⃣", "6️⃣"];
  let scoreString = "";

  const roundScores = scoreState.roundScores || [];
  roundScores.forEach((roundScore, roundIndex) => {
    scoreString += `${keycapEmojis[roundIndex]}`;

    roundScore.wordSpellings.forEach((wordSpelling) => {
      scoreString +=
        wordSpelling.userSpelling === wordSpelling.correctSpelling
          ? "✅"
          : "❌";
    });

    scoreString += `\n`;
  });

  return scoreString;
}

// function getHighestScoringWord(scoreState: ScoreState) {
//   let highestScore = -1;
//   let highestWord = "";
//   const roundScores = scoreState.roundScores || [];
//   roundScores.forEach((roundScore) => {
//     roundScore.wordSpellings.forEach((wordSpelling) => {
//       if (
//         wordSpelling.userSpelling === wordSpelling.correctSpelling &&
//         wordSpelling.pointValue != null &&
//         wordSpelling.pointValue > highestScore
//       ) {
//         highestScore = wordSpelling.pointValue;
//         highestWord = wordSpelling.correctSpelling;
//       }
//     });
//   });

//   return highestWord.toLocaleUpperCase();
// }

function getPreMultScore(scoreState: ScoreState): number {
  let total = 0;
  const roundScores = scoreState.roundScores || [];
  roundScores.forEach((roundScore) => {
    // In order to get the round score before the final multiplier was applied,
    // we have to skip the final round. (Which is the only round that uses a
    // multiplier).
    if (roundScore.multiplier == null) {
      total += roundScore.score;
    }
  });
  return total;
}

function getFinalRoundMultiplier(scoreState: ScoreState): number {
  const roundScores = scoreState.roundScores || [];
  for (const roundScore of roundScores) {
    if (roundScore.multiplier != null) {
      return roundScore.multiplier;
    }
  }
  return 1;
}
